/**
 * HandoffLogPage — full audit trail of shift handoff notes across all sites.
 * Foremen can add notes; supervisors see the full log.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FileText, Plus, RefreshCw, MapPin, Clock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";

export default function HandoffLogPage() {
  const { user } = useAuth();
  const [sites, setSites]   = useState([]);
  const [allShifts, setAllShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState("all");
  const [noteInputs, setNoteInputs] = useState({}); // shift_id → text
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const sites = await api.getSites().catch(() => []);
    setSites(sites);
    // Load all shifts for all sites
    const shiftArrays = await Promise.all(sites.map((s) => api.getShifts(s.site_id).catch(() => [])));
    const flat = shiftArrays.flat().sort((a, b) =>
      new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
    setAllShifts(flat);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addHandoffNote = async (shift) => {
    const note = (noteInputs[shift.shift_id] || "").trim();
    if (!note) return;
    setSaving((s) => ({ ...s, [shift.shift_id]: true }));
    try {
      const updated = await api.updateShift(shift.shift_id, { handoff_note: note });
      setAllShifts((prev) => prev.map((s) => s.shift_id === shift.shift_id ? updated : s));
      setNoteInputs((n) => ({ ...n, [shift.shift_id]: "" }));
    } catch {}
    setSaving((s) => ({ ...s, [shift.shift_id]: false }));
  };

  const siteMap = Object.fromEntries(sites.map((s) => [s.site_id, s.name]));
  const filtered = selectedSite === "all"
    ? allShifts
    : allShifts.filter((sh) => sh.site_id === selectedSite);

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-400" /> Handoff Log
        </h1>
        <div className="flex items-center gap-2">
          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500"
          >
            <option value="all">All Sites</option>
            {sites.map((s) => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
          </select>
          <button onClick={load} className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-400">No shifts found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((sh) => {
            const hasHandoff = sh.handoff_log?.length > 0;
            const isActive   = sh.status === "active";
            return (
              <div key={sh.shift_id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                {/* Shift Header */}
                <div className="flex items-start gap-3 p-4">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${isActive ? "bg-green-400" : "bg-gray-600"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-white">{sh.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        isActive ? "bg-green-500/20 text-green-400" : "bg-gray-700 text-gray-400"
                      }`}>{sh.status}</span>
                      {!sh.foreman_id && (
                        <span className="flex items-center gap-1 text-xs text-yellow-400">
                          <AlertTriangle className="w-3 h-3" /> No foreman
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      {siteMap[sh.site_id] && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {siteMap[sh.site_id]}
                        </span>
                      )}
                      {sh.start_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(sh.start_time).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                          {sh.end_time && ` → ${new Date(sh.end_time).toLocaleString([], { timeStyle: "short" })}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
                    {hasHandoff
                      ? <><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> {sh.handoff_log.length} note(s)</>
                      : <><AlertTriangle className="w-3.5 h-3.5 text-yellow-500" /> No handoff</>}
                  </div>
                </div>

                {/* Handoff Notes */}
                {hasHandoff && (
                  <div className="border-t border-gray-800 px-4 pb-3 pt-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Handoff Notes</p>
                    {sh.handoff_log.map((note, i) => (
                      <div key={i} className="bg-gray-800 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-500">
                            {note.recorded_by || "Foreman"}
                          </span>
                          {note.recorded_at && (
                            <span className="text-xs text-gray-600">
                              {new Date(note.recorded_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-200">{note.note || note}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Note Form (foreman/admin only) */}
                {(user?.role === "admin" || user?.role === "employer") && (
                  <div className="border-t border-gray-800 px-4 pb-4 pt-3 flex gap-2">
                    <input
                      value={noteInputs[sh.shift_id] || ""}
                      onChange={(e) => setNoteInputs((n) => ({ ...n, [sh.shift_id]: e.target.value }))}
                      placeholder="Add handoff note…"
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                    />
                    <button
                      disabled={saving[sh.shift_id]}
                      onClick={() => addHandoffNote(sh)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {saving[sh.shift_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                      Add
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
